import { runInAction } from 'mobx';
import { AffiliateUserWithDetails } from '@monorepo/client-types';
import { PaginationRequest } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchAffiliate,
  fetchAffiliateAvailableCommissions,
  fetchAffiliateCouponCode,
  fetchAffiliatePayoutsTotal,
  fetchAffiliateWithdrawalsCount,
  fetchUserAffiliationDashboard,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.AFFILIATES;

export const useUserAffiliationDashboard = () => {
  const {
    dataStore: { userStore },
  } = useStore();

  return useQuery<ExtractFnReturnType<typeof fetchUserAffiliationDashboard>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].DASHBOARD,
    queryFn: fetchUserAffiliationDashboard,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    meta: {
      onSuccess: (affiliateDashboard: AffiliateUserWithDetails) => {
        runInAction(() => {
          userStore.affiliateDashboard = affiliateDashboard;
        });
      },
    },
  });
};

export const useAffiliateAvailableSum = () => {
  return useQuery<
    ExtractFnReturnType<typeof fetchAffiliateAvailableCommissions>
  >({
    queryKey: QUERY_KEYS[CONFIG_KEY].AVAILABLE_SUM,
    queryFn: fetchAffiliateAvailableCommissions,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    meta: {
      onError: () => 0,
    },
  });
};

export const useAffiliatePayoutsTotal = (affiliateId: number) => {
  return useQuery<ExtractFnReturnType<typeof fetchAffiliatePayoutsTotal>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].PAYOUTS_TOTAL(affiliateId),
    queryFn: () => fetchAffiliatePayoutsTotal(affiliateId),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    meta: {
      onError: () => 0,
    },
    enabled: !!affiliateId,
  });
};

export const useAffiliateWithdrawalsCount = (affiliateId: number) => {
  return useQuery<ExtractFnReturnType<typeof fetchAffiliateWithdrawalsCount>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].WITHDRAWALS_COUNT(affiliateId),
    queryFn: () => fetchAffiliateWithdrawalsCount(affiliateId),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    meta: {
      onError: () => 0,
    },
    enabled: !!affiliateId,
  });
};

export const useAffiliate = (params: PaginationRequest) => {
  return useQuery<ExtractFnReturnType<typeof fetchAffiliate>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].AFFILIATE_LIST(params),
    queryFn: () => fetchAffiliate(params),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const useAffiliateCouponCode = (affiliateId: string) => {
  return useQuery<ExtractFnReturnType<typeof fetchAffiliateCouponCode>>({
    queryKey: [...QUERY_KEYS[CONFIG_KEY].DASHBOARD, 'coupon-code', affiliateId],
    queryFn: () => fetchAffiliateCouponCode(affiliateId),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    enabled: !!affiliateId,
  });
};
